import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import GoogleMapInput from '../../components/GoogleMapInput';
import Input from '../../components/input';
import AttachmentViewerModal from '../../components/modals/AttachmentViewerModal';
import Select from '../../components/Select';
import { fetchCustomerDropdown, fetchThreePlTransporterDropdown } from '../../store/Slices/dropdownSlice';
import { getFuelStations } from '../../store/Slices/FuelSlice';
import {
  createPettyCash,
  deletePettyCash,
  getPettyCashModes,
  getPettyCashSource,
  getTripDetails,
  updatePettyCash
} from '../../store/Slices/TripSlice';
import { formatDate } from '../../utils/helper';
import { pettyCashSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import moment from 'moment';
import { FiEdit, FiEye } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';

const toLabel = (item) =>
  String(
    item?.label ??
    item?.name ??
    item?.title ??
    item?.company_name ??
    item?.transporter_name ??
    item?.value ??
    item ??
    ""
  ).trim();

const toPositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const mapDropdownOptions = (source = [], fallback = []) => {
  const rows = Array.isArray(source) && source.length ? source : fallback;

  return rows
    .map((item) => {
      const label = toLabel(item);
      if (!label) return null;

      const rawValue = item?.value ?? item?.id ?? item?.vehicle_type_id ?? label;
      const recordId = toPositiveId(rawValue);

      return {
        label,
        value: recordId || rawValue,
        recordId,
      };
    })
    .filter(Boolean);
};

const normalizePettyCashSource = (value = '') =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const normalizePettyCashSourceForBackend = (value = '') =>
  String(value || '').trim().toLowerCase().replace(/[\s_]+/g, '-');

const normalizeOptionMatchValue = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const findMatchingOption = (options = [], value = '') => {
  const normalizedValue = normalizeOptionMatchValue(value);
  if (!normalizedValue) return null;

  return options.find((option) => {
    const normalizedLabel = normalizeOptionMatchValue(option?.label);
    const normalizedOptionValue = normalizeOptionMatchValue(option?.value);
    return normalizedLabel === normalizedValue || normalizedOptionValue === normalizedValue;
  }) || null;
};

const findOptionByIdentifier = (options = [], value = '') => {
  const identifier = String(value ?? '').trim();
  if (!identifier) return null;

  return options.find((option) => String(option?.value ?? option?.id ?? '').trim() === identifier) || null;
};

const mapUploadedFilesForPayload = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map((file) => {
      if (typeof file === 'string') return file;
      return file?.fileName || file?.file_path || file?.file_name || '';
    })
    .filter(Boolean);

const getCurrentAssignAt = () => moment().format('YYYY-MM-DDTHH:mm');

const getAvailableAmountError = (amount, availableAmount) => {
  if (amount === '' || amount === null || amount === undefined) return '';

  const enteredAmount = Number(amount);
  if (!Number.isFinite(enteredAmount)) return '';

  if (enteredAmount <= 0) {
    return 'Amount must be greater than 0';
  }

  if (enteredAmount > Number(availableAmount || 0)) {
    return `Amount cannot exceed available PKR ${Number(availableAmount || 0).toLocaleString()}`;
  }

  return '';
};

const createEmptyPettyCashValues = (assignAt = getCurrentAssignAt()) => ({
  amount: '',
  payment_mode: '',
  transaction_id: '',
  assign_at: assignAt,
  source: '',
  source_id: '',
  location: {
    point: '',
    latitude: '',
    longitude: '',
  },
  lat: '',
  lng: '',
  description: ''
});

const isDropdownSource = (source) => {
  const normalizedSource = normalizePettyCashSource(source);
  return (
    normalizedSource === 'filling_station' ||
    normalizedSource === 'customer' ||
    normalizedSource === 'transporter'
  );
};

const isLocationSource = (source) => !isDropdownSource(source);

const getSourceDependentLabel = (source) => {
  const normalizedSource = normalizePettyCashSource(source);
  if (normalizedSource === 'filling_station') return 'Filling Station';
  if (normalizedSource === 'customer') return 'Customer';
  if (normalizedSource === 'transporter') return 'Transporter';
  return 'Source';
};

const getSourceDependentPlaceholder = (source) => {
  const normalizedSource = normalizePettyCashSource(source);
  if (normalizedSource === 'filling_station') return 'Select Filling Station';
  if (normalizedSource === 'customer') return 'Select Customer';
  if (normalizedSource === 'transporter') return 'Select Transporter';
  return 'Select Source';
};

const TripPettyCash = ({
  tripPettyCash,
  vehicle_id,
  isTripCompleted = false,
  disablePettyCashActions = false,
  pettyCashLockReason = '',
  allocatedPettyCashAmount = 0,
  totalPettyCashAmount = 0,
  canAddPettyCash = true,
  canEditPettyCash = true,
  canDeletePettyCash = true,
  trip_start_at,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [showAddItemBtn, setShowAddItemBtn] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [attachmentPreviewFiles, setAttachmentPreviewFiles] = useState([]);

  const {
    createPettyCashLoading,
    updatePettyCashLoading,
    pettyCashModesData,
    pettyCashSourceData,
    tripDetailsData
  } = useSelector((state) => state.trips);

  const effectiveTripStart = trip_start_at ||
    tripDetailsData?.actual_start_at ||
    tripDetailsData?.started_at ||
    tripDetailsData?.dispatch_at ||
    tripDetailsData?.start_at ||
    tripDetailsData?.created_at;

  const minAssignAt = effectiveTripStart ? moment(effectiveTripStart).format('YYYY-MM-DDTHH:mm') : null;
  const tripStartDisplay = effectiveTripStart ? moment(effectiveTripStart).format('DD/MM/YYYY, HH:mm') : null;

  const [defaultAssignAt, setDefaultAssignAt] = useState(() => {
    const current = getCurrentAssignAt();
    if (effectiveTripStart && moment(current).isBefore(moment(effectiveTripStart))) {
      return moment(effectiveTripStart).format('YYYY-MM-DDTHH:mm');
    }
    return current;
  });

  useEffect(() => {
    if (effectiveTripStart) {
      const current = getCurrentAssignAt();
      if (moment(current).isBefore(moment(effectiveTripStart))) {
        setDefaultAssignAt(moment(effectiveTripStart).format('YYYY-MM-DDTHH:mm'));
      }
    }
  }, [effectiveTripStart]);

  const {
    customerDropdownData = [],
    threePlTransporterDropdownData = [],
    loading: dropdownLoading
  } = useSelector((state) => state.dropdowns || {});
  const { getFuelStationsData = [], getFuelStationsLoading } = useSelector((state) => state.fuel || {});

  const transporterOptions = useMemo(
    () => mapDropdownOptions(threePlTransporterDropdownData),
    [threePlTransporterDropdownData]
  );

  const pettyCashModesOptions = (Array.isArray(pettyCashModesData) ? pettyCashModesData : []).map((item) => ({
    value: item.id,
    label: item.title
  }));

  const pettyCashSourceOptions = (Array.isArray(pettyCashSourceData) ? pettyCashSourceData : []).map((item) => ({
    value: item.id,
    label: item.title
  }));

  const currentEditableAmount = useMemo(() => {
    return Number(editData?.amount || 0);
  }, [editData]);

  const availablePettyCashAmount = useMemo(() => {
    if (isEditing) {
      return Math.max(Number(allocatedPettyCashAmount || 0) - Number(totalPettyCashAmount || 0) + currentEditableAmount, 0);
    }
    return Math.max(Number(allocatedPettyCashAmount || 0) - Number(totalPettyCashAmount || 0), 0);
  }, [isEditing, allocatedPettyCashAmount, totalPettyCashAmount, currentEditableAmount]);

  useEffect(() => {
    dispatch(getPettyCashModes());
    dispatch(getPettyCashSource());
    dispatch(getFuelStations());
    dispatch(fetchCustomerDropdown());
    dispatch(fetchThreePlTransporterDropdown());
  }, [dispatch]);

  const initialValues = editData ? {
    amount: editData.amount || '',
    payment_mode: findMatchingOption(pettyCashModesOptions, editData.payment_mode)?.label || editData.payment_mode || '',
    transaction_id: editData.transaction_id || '',
    assign_at: editData.assign_at ? moment(editData.assign_at).format('YYYY-MM-DDTHH:mm') : defaultAssignAt,
    source: findMatchingOption(pettyCashSourceOptions, editData.source)?.label || editData.source || '',
    source_id: editData.source_id ?? '',
    location: {
      point: editData?.location || '',
      latitude: editData?.lat || '',
      longitude: editData?.lng || '',
    },
    lat: editData?.lat || '',
    lng: editData?.lng || '',
    description: editData.description || ''
  } : createEmptyPettyCashValues(defaultAssignAt);

  const isCashPaymentMode = (paymentMode) => {
    const normalizedMode = String(paymentMode || '').trim().toLowerCase().replace(/\s+/g, '_');
    return normalizedMode === 'cash';
  };

  const shouldShowTransactionId = (paymentMode) => {
    const normalizedMode = String(paymentMode || '').trim();
    if (!normalizedMode) return false;
    return !isCashPaymentMode(paymentMode);
  };

  const handlePettyCashSubmit = async (values, { setSubmitting, resetForm, setFieldError, setFieldTouched }) => {
    try {
      if (disablePettyCashActions) {
        errorNotify('Petty cash cannot be added or edited after trip completion.');
        setSubmitting(false);
        return;
      }

      if (isEditing ? !canEditPettyCash : !canAddPettyCash) {
        errorNotify(`You do not have permission to ${isEditing ? 'edit' : 'add'} petty cash logs.`);
        setSubmitting(false);
        return;
      }

      const resolvedVehicleId = Number(
        vehicle_id
        || tripDetailsData?.vehicle_id
        || editData?.vehicle_id
      );

      if (!Number.isFinite(resolvedVehicleId) || resolvedVehicleId <= 0) {
        errorNotify('Vehicle ID is missing. Please refresh trip details and try again.');
        setSubmitting(false);
        return;
      }

      const enteredAmount = Number(values.amount || 0);

      if (enteredAmount <= 0 || enteredAmount > availablePettyCashAmount) {
        setFieldTouched('amount', true, false);
        setFieldError('amount', getAvailableAmountError(values.amount, availablePettyCashAmount));
        setSubmitting(false);
        return;
      }

      if (effectiveTripStart && values.assign_at) {
        if (moment(values.assign_at).isBefore(moment(effectiveTripStart))) {
          setFieldTouched('assign_at', true, false);
          setFieldError('assign_at', `Date & Time cannot be earlier than trip start (${tripStartDisplay || minAssignAt})`);
          errorNotify(`Petty Cash date & time cannot be earlier than trip start date & time (${tripStartDisplay || minAssignAt})`);
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        vehicle_id: resolvedVehicleId,
        trip_id: parseInt(id),
        amount: parseFloat(values.amount),
        payment_mode: values.payment_mode,
        transaction_id: values.transaction_id || undefined,
        assign_at: values.assign_at ? formatDate(values.assign_at) : null,
        source: normalizePettyCashSourceForBackend(values.source),
        source_id: isDropdownSource(values.source)
          ? Number(values.source_id || 0)
          : 0,
        description: values.description,
        files: mapUploadedFilesForPayload(uploadedFiles)
      };

      if (isLocationSource(values.source)) {
        payload.location = values.location.point;
        payload.lat = values.location.latitude;
        payload.lng = values.location.longitude;
      }

      if (isEditing) {
        payload.petty_cash_id = editId;
        await dispatch(updatePettyCash(payload)).unwrap();
        successNotify('Petty cash log updated successfully');
      } else {
        await dispatch(createPettyCash(payload)).unwrap();
        successNotify('Petty cash log added successfully');
      }

      dispatch(getTripDetails(id));

      resetForm();
      setShowAddItemBtn(false);
      setUploadedFiles([]);
      setDefaultAssignAt(getCurrentAssignAt());
      setIsEditing(false);
      setEditId(null);
      setEditData(null);
    } catch (error) {
      errorNotify(error?.message || `Failed to ${isEditing ? 'update' : 'add'} petty cash log`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    if (disablePettyCashActions || !canEditPettyCash) return;

    setEditData({
      amount: item.amount,
      payment_mode: item.payment_mode,
      transaction_id: item.transaction_id || '',
      assign_at: item.assign_at,
      source: item.source,
      source_id: item.source_id ?? '',
      location: item.location,
      lat: item.lat,
      lng: item.lng,
      description: item.description,
      files: Array.isArray(item?.files) ? item.files : []
    });
    setUploadedFiles(Array.isArray(item?.files) ? item.files : []);
    setEditId(item.id);
    setIsEditing(true);
    setShowAddItemBtn(true);
  };

  const handleDelete = async (itemId) => {
    if (disablePettyCashActions || !canDeletePettyCash) return;

    try {
      await dispatch(deletePettyCash(itemId)).unwrap();
      successNotify('Petty cash log deleted successfully');
      dispatch(getTripDetails(id));
    } catch (error) {
      errorNotify(error?.message || 'Failed to delete petty cash log');
    }
  };

  const handleCancel = () => {
    setShowAddItemBtn(false);
    setUploadedFiles([]);
    setDefaultAssignAt(getCurrentAssignAt());
    setIsEditing(false);
    setEditId(null);
    setEditData(null);
  };

  const openAttachmentViewer = (files = []) => {
    const nextFiles = Array.isArray(files) ? files : [];
    if (!nextFiles.length) {
      errorNotify('No attachments found');
      return;
    }

    setAttachmentPreviewFiles(nextFiles);
  };

  const resolveLocationDisplay = (item) => {
    if (item?.location) return item.location;
    if (item?.transporter_name) return item.transporter_name;
    if (item?.customer_name) return item.customer_name;
    if (item?.station_name) return item.station_name;

    const normalizedSource = normalizePettyCashSource(item?.source);
    if (normalizedSource === 'transporter' && item?.source_id) {
      const matched = transporterOptions.find((opt) => String(opt.value) === String(item.source_id));
      if (matched?.label) return matched.label;
    }
    if (normalizedSource === 'customer' && item?.source_id) {
      const matched = (Array.isArray(customerDropdownData) ? customerDropdownData : []).find(
        (opt) => String(opt.value ?? opt.id) === String(item.source_id)
      );
      if (matched?.label) return matched.label;
    }
    if (normalizedSource === 'filling_station' && item?.source_id) {
      const matched = (Array.isArray(getFuelStationsData) ? getFuelStationsData : []).find(
        (opt) => String(opt.value ?? opt.id) === String(item.source_id)
      );
      if (matched?.label) return matched.label;
    }

    return '-';
  };

  return (
    <div className='service_line_items_main' style={{ border: 'none', padding: '0px' }}>
      <div className='service_line_items_table' style={{ marginBottom: '20px' }}>
        <Table responsive>
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount (PKR)</th>
              <th>Payment Mode</th>
              <th>Date</th>
              <th>Source</th>
              <th>Location</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {
              tripPettyCash?.length > 0 ? (
                tripPettyCash.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description || '-'}</td>
                    <td>{item.amount?.toLocaleString() || '0'}</td>
                    <td>{item.payment_mode ? item.payment_mode.replace('_', ' ') : '-'}</td>
                    <td>{formatDate(item.assign_at)}</td>
                    <td>{item.source ? item.source : '-'}</td>
                    <td>{resolveLocationDisplay(item)}</td>
                    <td>
                      <div className='d-flex gap-2'>
                        {canEditPettyCash && (
                          <FiEdit
                            style={{
                              cursor: disablePettyCashActions ? 'not-allowed' : 'pointer',
                              color: disablePettyCashActions ? '#BDBDBD' : '#4CAF50'
                            }}
                            onClick={() => handleEdit(item)}
                          />
                        )}
                        {canDeletePettyCash && (
                          <RiDeleteBinLine
                            style={{
                              cursor: disablePettyCashActions ? 'not-allowed' : 'pointer',
                              color: disablePettyCashActions ? '#BDBDBD' : '#F44336'
                            }}
                            onClick={() => handleDelete(item.id)}
                          />
                        )}
                        {Array.isArray(item?.files) && item.files.length > 0 && (
                          <button
                            type='button'
                            className='trip_attachment_view_btn'
                            onClick={() => openAttachmentViewer(item.files)}
                            title='View attachments'
                          >
                            <FiEye /> {item.files.length}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='text-center' style={{ padding: '20px', color: '#888' }}>
                    No petty cash logs found
                  </td>
                </tr>
              )
            }
          </tbody>
        </Table>
      </div>

      {!showAddItemBtn && canAddPettyCash && (
        <>
          <button
            className='add_issue_btn'
            type='button'
            disabled={disablePettyCashActions}
            onClick={() => {
              if (disablePettyCashActions) return;
              setEditData(null);
              setUploadedFiles([]);
              setDefaultAssignAt(getCurrentAssignAt());
              setIsEditing(false);
              setShowAddItemBtn(true);
            }}
          >
            + Add Petty Cash Log
          </button>

          {disablePettyCashActions && pettyCashLockReason && (
            <p style={{ marginTop: '10px', color: '#D32F2F', fontSize: '13px' }}>
              {pettyCashLockReason}
            </p>
          )}
        </>
      )}

      <div className='add_petty_cash'>
        {
          showAddItemBtn &&
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={pettyCashSchema}
            validate={(values) => {
              const validationErrors = {};
              const amountLimitError = getAvailableAmountError(
                values.amount,
                availablePettyCashAmount
              );

              if (amountLimitError) {
                validationErrors.amount = amountLimitError;
              }

              if (effectiveTripStart && values.assign_at) {
                if (moment(values.assign_at).isBefore(moment(effectiveTripStart))) {
                  validationErrors.assign_at = `Date & Time cannot be earlier than trip start (${tripStartDisplay || minAssignAt})`;
                }
              }

              return validationErrors;
            }}
            onSubmit={handlePettyCashSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, submitCount }) => {
              const amountLimitError = getAvailableAmountError(
                values.amount,
                availablePettyCashAmount
              );
              const amountError = amountLimitError || errors.amount;
              const showAmountError = Boolean(touched.amount || submitCount > 0 || amountLimitError);

              return (
              <div>
                <h4>{isEditing ? 'Edit Petty Cash Log' : 'Add Petty Cash Log'}</h4>
                <Row>
                  <Col md={6}>
                    <Input
                      label='Amount (PKR)'
                      type='number'
                      placeholder='00'
                      name='amount'
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={amountError}
                      touched={showAmountError}
                    />
                    <small style={{ color: "#2E7D32", marginBottom: "14px", display: "block" }}>
                      Available: PKR {availablePettyCashAmount.toLocaleString()}
                    </small>
                  </Col>

                  <Col md={6}>
                    <Select
                      label='Payment Mode'
                      placeholder='Select Payment Mode'
                      options={pettyCashModesOptions}
                      name='payment_mode'
                      value={findMatchingOption(pettyCashModesOptions, values.payment_mode)}
                      onChange={(opt) => {
                        const nextMode = opt ? opt.label : '';
                        setFieldValue('payment_mode', nextMode);
                        if (isCashPaymentMode(nextMode)) {
                          setFieldValue('transaction_id', '');
                        }
                      }}
                      onBlur={handleBlur}
                      error={errors.payment_mode}
                      touched={touched.payment_mode}
                      isSearch={false}
                    />
                  </Col>

                  {shouldShowTransactionId(values.payment_mode) && (
                    <Col md={6}>
                      <Input
                        label='Transaction ID'
                        type='text'
                        placeholder='Enter Transaction ID'
                        name='transaction_id'
                        value={values.transaction_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.transaction_id}
                        touched={touched.transaction_id}
                      />
                    </Col>
                  )}

                  <Col md={6}>
                    <Input
                      label='Created Date & Time'
                      type='datetime-local'
                      name='assign_at'
                      value={values.assign_at}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.assign_at}
                      touched={touched.assign_at}
                      min={minAssignAt}
                      minValue={minAssignAt}
                      disabled={false}
                    />
                  </Col>

                  <Col md={6}>
                    <Select
                      label='Source'
                      placeholder='Select Source'
                      options={pettyCashSourceOptions}
                      name='source'
                      value={findMatchingOption(pettyCashSourceOptions, values.source)}
                      onChange={(opt) => {
                        const nextSource = opt ? opt.label : '';
                        setFieldValue('source', nextSource);
                        if (isDropdownSource(nextSource)) {
                          setFieldValue('source_id', '');
                          setFieldValue('location', {
                            point: '',
                            latitude: '',
                            longitude: '',
                          });
                        } else {
                          setFieldValue('source_id', 0);
                        }
                      }}
                      onBlur={handleBlur}
                      error={errors.source}
                      touched={touched.source}
                      isSearch={false}
                    />
                  </Col>

                  {isDropdownSource(values.source) ? (
                    <Col md={shouldShowTransactionId(values.payment_mode) ? 6 : 12}>
                      <Select
                        label={getSourceDependentLabel(values.source)}
                        placeholder={getSourceDependentPlaceholder(values.source)}
                        options={
                          normalizePettyCashSource(values.source) === 'filling_station'
                            ? (Array.isArray(getFuelStationsData) ? getFuelStationsData : [])
                            : normalizePettyCashSource(values.source) === 'customer'
                            ? (Array.isArray(customerDropdownData) ? customerDropdownData : [])
                            : transporterOptions
                        }
                        loading={
                          normalizePettyCashSource(values.source) === 'filling_station'
                            ? getFuelStationsLoading
                            : normalizePettyCashSource(values.source) === 'transporter'
                            ? dropdownLoading
                            : false
                        }
                        name='source_id'
                        value={
                          findOptionByIdentifier(
                            normalizePettyCashSource(values.source) === 'filling_station'
                              ? (Array.isArray(getFuelStationsData) ? getFuelStationsData : [])
                              : normalizePettyCashSource(values.source) === 'customer'
                              ? (Array.isArray(customerDropdownData) ? customerDropdownData : [])
                              : transporterOptions,
                            values.source_id
                          )
                        }
                        onChange={(opt) => setFieldValue('source_id', opt?.value || '')}
                        onBlur={handleBlur}
                        error={errors.source_id}
                        touched={touched.source_id}
                        isSearch={true}
                      />
                    </Col>
                  ) : (
                    <Col md={shouldShowTransactionId(values.payment_mode) ? 6 : 12}>
                      <GoogleMapInput
                        label='Location'
                        name='location'
                        fieldPrefix=''
                        value={values.location}
                        setFieldValue={setFieldValue}
                        error={errors.location}
                        touched={touched.location}
                      />
                    </Col>
                  )}

                  <Col md={12}>
                    <div className='input_wrapper'>
                      <div className='input_container'>
                        <label>Description (Optional)</label>
                        <textarea
                          rows={3}
                          placeholder='Add notes or additional details'
                          name='description'
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={{
                            width: '100%',
                            border: '1px solid #D0D5DD',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            outline: 'none'
                          }}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="photo_document_section" style={{ border: "none", padding: "0", marginTop: "0" }}>
                      <FileUpload
                        destination="fuel-logs"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                        tripId={id}
                      />
                    </div>
                  </Col>

                  <Col md={12} className="mt-3">
                    <div className='d-flex gap-3'>
                      <Button
                        cancelBtn={handleCancel}
                        loading={createPettyCashLoading || updatePettyCashLoading}
                        disabled={createPettyCashLoading || updatePettyCashLoading || Boolean(amountLimitError)}
                        submitBtn={handleSubmit}
                        submitText={isEditing ? "Update" : "Save"}
                        shouldRow={false}
                        buttonExtraCss="w-100"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              );
            }}
          </Formik>
        }
      </div>
      <AttachmentViewerModal
        show={attachmentPreviewFiles.length > 0}
        files={attachmentPreviewFiles}
        heading='Petty Cash Attachments'
        handleClose={() => setAttachmentPreviewFiles([])}
      />
    </div>
  );
};

export default TripPettyCash;
